import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { authApi, type SessionDto } from "../api/authApi";
import { useBrand } from "../components/brand/BrandProvider";
import { setReportTimeZone } from "../components/reports/ReportBits";

interface SessionContextValue {
  session: SessionDto | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setSession: (session: SessionDto | null) => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await authApi.session();
      setSession(result?.isAuthenticated ? result : null);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The resolved brand follows the signed-in customer (manager colour,
  // logo, name), so re-fetch it whenever the session identity changes:
  // login, logout, impersonate, switch back. The first resolution is
  // skipped because the server-rendered shell already painted the brand
  // for the session that existed at page load.
  const { refresh: refreshBrand } = useBrand();
  const identity = session?.isAuthenticated ? `${session.kind}:${session.customerId ?? ""}:${session.managerId ?? ""}` : "";
  const lastIdentity = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (lastIdentity.current === null) {
      lastIdentity.current = identity;
      return;
    }
    if (lastIdentity.current === identity) return;
    lastIdentity.current = identity;
    refreshBrand().catch(() => undefined);
  }, [identity, loading, refreshBrand]);

  // Report formatters render in the customer's time zone.
  setReportTimeZone(session?.isAuthenticated ? session.timeZoneIana : "UTC");

  const value = useMemo<SessionContextValue>(
    () => ({ session, loading, refresh, setSession }),
    [session, loading, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return ctx;
}
